import { useEffect, useState } from "react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BellRing, Save, Play, AlertTriangle } from "lucide-react";

interface Alert {
  id: string;
  student_id: string;
  field_group_id: string;
  semester: string;
  class: string;
  rate_pct: number;
  threshold_pct: number;
  scheduled_days: number;
  present_days: number;
  created_at: string;
  students?: { first_name: string; last_name: string; student_number: string | null } | null;
  field_groups?: { name: string } | null;
}

export function RotationEligibilityAlerts() {
  const { hasRole, isTopManagement, user } = useAuth();
  const canManage = isTopManagement() || hasRole("head_academic") || hasRole("head_administration");
  const isSuperadmin = hasRole("superadmin");

  const [threshold, setThreshold] = useState<number>(80);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [running, setRunning] = useState(false);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(false);

  async function loadThreshold() {
    const { data } = await supabase
      .from("system_settings")
      .select("id,value")
      .eq("key", "rotation_alert_threshold")
      .maybeSingle();
    if (data) {
      setSettingId(data.id);
      const v = data.value as any;
      if (v && typeof v.percent === "number") setThreshold(v.percent);
    }
  }

  async function loadAlerts() {
    setLoading(true);
    const { data, error } = await supabase
      .from("rotation_attendance_alerts")
      .select("*, students(first_name,last_name,student_number), field_groups(name)")
      .order("created_at", { ascending: false })
      .limit(50);
    if (!error) setAlerts((data ?? []) as any);
    setLoading(false);
  }

  useEffect(() => {
    if (!canManage) return;
    loadThreshold();
    loadAlerts();
  }, [canManage]);

  async function saveThreshold() {
    if (!isSuperadmin) {
      toast.error("Only superadmins can change the threshold.");
      return;
    }
    if (threshold < 1 || threshold > 100) {
      toast.error("Threshold must be between 1 and 100.");
      return;
    }
    setSavingThreshold(true);
    const payload = { percent: threshold } as any;
    const res = settingId
      ? await supabase.from("system_settings")
          .update({ value: payload, updated_by: user?.id }).eq("id", settingId)
      : await supabase.from("system_settings")
          .insert([{ key: "rotation_alert_threshold", value: payload, updated_by: user?.id }]);
    setSavingThreshold(false);
    if (res.error) { toast.error(res.error.message); return; }
    toast.success(`Threshold saved at ${threshold}%`);
    loadThreshold();
  }

  async function runCheck() {
    setRunning(true);
    const { data, error } = await supabase.rpc("check_rotation_eligibility_alerts" as any);
    setRunning(false);
    if (error) { toast.error(error.message); return; }
    const row: any = Array.isArray(data) ? data[0] : data;
    const created = row?.alerts_created ?? 0;
    const below = row?.students_below ?? 0;
    toast.success(`${created} new alert${created === 1 ? "" : "s"} sent · ${below} student${below === 1 ? "" : "s"} below threshold`);
    loadAlerts();
  }

  if (!canManage) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BellRing className="w-4 h-4" /> Rotation Eligibility Alerts
        </CardTitle>
        <CardDescription>
          Notify principals and heads when a student's Rotation attendance drops below the configured threshold for the semester and course.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs">Alert threshold (%)</Label>
            <Input
              type="number" min={1} max={100}
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              disabled={!isSuperadmin}
            />
            {!isSuperadmin && (
              <p className="text-[11px] text-muted-foreground mt-1">Only superadmins can change this.</p>
            )}
          </div>
          <div>
            <Button
              variant="secondary"
              className="w-full"
              onClick={saveThreshold}
              disabled={!isSuperadmin || savingThreshold}
            >
              <Save className="w-4 h-4 mr-1" /> {savingThreshold ? "Saving…" : "Save threshold"}
            </Button>
          </div>
          <div>
            <Button className="w-full" onClick={runCheck} disabled={running}>
              <Play className="w-4 h-4 mr-1" /> {running ? "Checking…" : "Run check now"}
            </Button>
          </div>
        </div>

        <div>
          <p className="text-sm font-medium mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-muted-foreground" /> Recent alerts
          </p>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Student</TableHead>
                <TableHead>Reg #</TableHead>
                <TableHead>Group</TableHead>
                <TableHead>Class · Sem</TableHead>
                <TableHead>Rate</TableHead>
                <TableHead>Triggered</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">Loading…</TableCell></TableRow>
              ) : alerts.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-6">No alerts yet. Run a check after groups have placement days recorded.</TableCell></TableRow>
              ) : alerts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>{a.students ? `${a.students.first_name} ${a.students.last_name}` : "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{a.students?.student_number ?? "—"}</TableCell>
                  <TableCell>{a.field_groups?.name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{a.class} · Sem {a.semester}</TableCell>
                  <TableCell>
                    <Badge variant="destructive">
                      {Number(a.rate_pct).toFixed(0)}% · {a.present_days}/{a.scheduled_days}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{format(new Date(a.created_at), "PP p")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
